// Hidden fields are form fields the visitor never sees: the store declares one
// in the form builder, and its value comes from the query string instead of an
// input. The marketing site appends the parameter to the signup link — an ad
// click id, a partner ref — so the customer created at the end of the funnel
// can be traced back to what brought them in.
//
// The field's own name is the parameter name, and the value is stored verbatim
// under that key in the customer's custom_fields, so what arrives is capped and
// trimmed here rather than passed straight through.
export const HIDDEN_FIELD_TYPE = "hidden";

// Long enough for a uuid, a click id or a signed token, short enough that a
// hand-built URL can't push an essay into the customer record.
const MAX_VALUE_LENGTH = 255;

/**
 * @param {Object} field - A form field from form.content.fields.
 * @returns {boolean} True when the field is filled from the URL, not by the visitor.
 */
export function isHiddenField(field) {
  return field?.type === HIDDEN_FIELD_TYPE;
}

/**
 * Collect the submission values for a form's hidden fields.
 * @param {Array} fields - form.content.fields, or nothing while the form loads.
 * @param {URLSearchParams} searchParams - The current query string.
 * @returns {Object} Field name to value, holding only the parameters actually present.
 */
export function collectHiddenFieldValues(fields, searchParams) {
  return (fields ?? []).filter(isHiddenField).reduce((values, field) => {
    // Absent is the common case — a hidden field collects nothing from organic
    // traffic — and an absent key keeps that customer out of the column
    // entirely, rather than filing an empty string against them.
    const value = searchParams.get(field.field)?.trim();
    if (!value) return values;

    return { ...values, [field.field]: value.slice(0, MAX_VALUE_LENGTH) };
  }, {});
}
